import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "chart.js/auto";
import moment from "moment";
import QueryBlock from "../../components/QueryBlock";
import { Bar } from "react-chartjs-2";
import { CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { getCumulativeCount } from "../../apis/PeopleCount.Api";
import { getPeopleCountLog } from "../../apis/PeopleCount.Api";

const FootfallBarChart = ({ selectedBuilding }) => {
    const userData = useSelector((state) => state.userData.data);
    const devices = useSelector((state) => state.device.data);
    const rooms = useSelector((state) => state.room.data);

    const { enqueueSnackbar } = useSnackbar();

    const [queryStartDate, setQueryStartDate] = useState("");
    const [queryEndDate, setQueryEndDate] = useState("");
    const [queryHour, setQueryHour] = useState(null);
    const [queryGender, setQueryGender] = useState("ALL");
    const [queryRooms, setQueryRooms] = useState([]);
    const [statistics, setStatistics] = useState({
        total: 0,
        average: 0,
        max: 0,
        min: 0,
    });
    const [labels, setLabels] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    async function handleSubmit() {
        setIsLoading(true);

        let realRooms =
            queryRooms.length > 0
                ? [...queryRooms]
                : Object.values(rooms)
                      .filter((room) => room.buildingId === selectedBuilding)
                      .map((room) => room.roomId);

        realRooms.sort((a, b) => {
            const aRawFloor = a.split("-")[1];
            const bRawFloor = b.split("-")[1];

            let aFloor = aRawFloor?.replace("B", "-") || "";
            let bFloor = bRawFloor?.replace("B", "-") || "";

            if (aFloor.endsWith("F")) {
                aFloor = aFloor.substring(0, aFloor.length - 1);
            }

            if (bFloor.endsWith("F")) {
                bFloor = bFloor.substring(0, bFloor.length - 1);
            }

            if (Number(bFloor) === Number(aFloor)) {
                const aRawGender = a.split("-")[2];

                if (aRawGender === "M") {
                    return 1;
                } else {
                    return -1;
                }
            }

            return aRawFloor.startsWith("B") && bRawFloor.startsWith("B")
                ? Number(bFloor) - Number(aFloor)
                : Number(aFloor) - Number(bFloor);
        });

        realRooms =
            queryGender === "ALL"
                ? [...realRooms]
                : [...realRooms].filter((roomId) => {
                      const splitArr = roomId.split("-");
                      return splitArr[2]?.startsWith(queryGender[0]);
                  });

        if (!queryHour) {
            try {
                const res = await getCumulativeCount({
                    room_id: JSON.stringify(realRooms),
                    start_time: moment(
                        moment(queryStartDate, "YYYYMMDD").format("YYYY/MM/DD") + " 00:00:00.000"
                    ).format("YYMMDDHHmmssSSS"),
                    end_time: moment(moment(queryEndDate, "YYYYMMDD").format("YYYY/MM/DD") + " 23:59:59.999").format(
                        "YYMMDDHHmmssSSS"
                    ),
                });

                if (!res.data.indicator) {
                    enqueueSnackbar(res.data.message, { variant: "error" });
                    setIsLoading(false);
                    return;
                }

                setLabels(realRooms);
                setDatasets([
                    {
                        data: realRooms
                            .map((roomId) => {
                                return res.data.message
                                    .filter((data) => data.room_id === roomId)
                                    .map((data) => data.cumulative_count);
                            })
                            .flat(),
                        backgroundColor: realRooms.map((roomId, index) => {
                            if (index % 2 === 0) {
                                return "#d9d9d9";
                            } else {
                                return "#cbd674";
                            }
                        }),
                    },
                ]);

                const total = res.data.message?.reduce((acc, data) => {
                    return acc + data.cumulative_count;
                }, 0);
                setStatistics({
                    total: total,
                    average: (total / realRooms.length).toFixed(2),
                    max: Math.max(...(res.data.message?.map((d) => d.cumulative_count) || [])),
                    min: Math.min(...(res.data.message?.map((d) => d.cumulative_count) || [])),
                });
            } catch (error) {
                console.log(error);
                enqueueSnackbar("Failed to load data", { variant: "error" });
            }
        } else {
            const roomToDevice = {};
            const deviceToRoom = {};

            realRooms.forEach((roomId) => {
                const device = Object.values(devices).find((device) => device.roomId === roomId);
                roomToDevice[roomId] = device?.deviceId;
                deviceToRoom[device?.deviceId] = roomId;
            });

            try {
                const res = await getPeopleCountLog({
                    device_id: JSON.stringify(Object.values(roomToDevice)),
                    start_time: moment(
                        moment(queryStartDate, "YYYYMMDD").format("YYYY/MM/DD") + " 00:00:00.000"
                    ).format("YYMMDDHHmmssSSS"),
                    start_time: moment(moment(queryEndDate, "YYYYMMDD").format("YYYY/MM/DD") + " 23:59:59.999").format(
                        "YYMMDDHHmmssSSS"
                    ),
                });

                if (!res.data.indicator) {
                    enqueueSnackbar(res.data.message, { variant: "error" });
                    setIsLoading(false);
                    return;
                }

                const counts = res.data.message;
                const chartData = Object.fromEntries(realRooms?.map((roomId) => [roomId, 0]));

                for (let count of counts) {
                    const rid = count.room_id;
                    if (rooms[rid].buildingId !== selectedBuilding) continue;
                    const time = moment(count.update_time, "YYMMDDHHmmssSSS").toDate();
                    const hour = time.getHours();

                    if (hour === queryHour.$H) {
                        chartData[rid] += count.entry;
                    }
                }

                setLabels(realRooms);
                setDatasets([
                    {
                        data: realRooms.map((roomId) => {
                            return chartData[roomId];
                        }),
                        backgroundColor: realRooms.map((roomId, index) => {
                            if (index % 2 === 0) {
                                return "#d9d9d9";
                            } else {
                                return "#cbd674";
                            }
                        }),
                    },
                ]);

                const total = Object.values(chartData)?.reduce((acc, count) => {
                    return acc + count;
                }, 0);
                setStatistics({
                    total: total,
                    average: (total / realRooms.length).toFixed(2),
                    max: Math.max(...Object.values(chartData)),
                    min: Math.min(...Object.values(chartData)),
                });
            } catch (error) {
                console.log(error);
                enqueueSnackbar("Failed to load data", { variant: "error" });
            }
        }
        setIsLoading(false);
    }

    useEffect(() => {
        setLabels([]);
        setDatasets([]);
        setStatistics({
            total: 0,
            average: 0,
            max: 0,
            min: 0,
        });
    }, [selectedBuilding]);
    return (
        <div className="footfall-chart">
            <QueryBlock
                selectedBuilding={selectedBuilding}
                enableQuerySensors={false}
                queryStartDate={queryStartDate}
                setQueryStartDate={setQueryStartDate}
                queryEndDate={queryEndDate}
                setQueryEndDate={setQueryEndDate}
                queryHour={queryHour}
                setQueryHour={setQueryHour}
                queryGender={queryGender}
                setQueryGender={setQueryGender}
                queryRooms={queryRooms}
                setQueryRooms={setQueryRooms}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
            />
            <div className="chart-graph">
                <div className="chart-graph-content">
                    {isLoading ? (
                        <CircularProgress color="inherit" />
                    ) : (
                        <Bar
                            title="Footfall"
                            data={{
                                labels: labels,
                                datasets: datasets,
                            }}
                            options={{
                                animation: {
                                    duration: 1000,
                                },
                                indexAxis: "y",
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                    },
                                },
                                plugins: {
                                    legend: {
                                        display: false,
                                    },
                                    annotation:
                                        statistics.average > 0
                                            ? {
                                                  annotations: {
                                                      verticalLine: {
                                                          type: "line",
                                                          xMin: statistics.average,
                                                          xMax: statistics.average,
                                                          borderColor: "gray",
                                                          borderWidth: 2,
                                                          borderDash: [5, 5],
                                                          label: {
                                                              content: "Average",
                                                              enabled: true,
                                                              position: "bottom",
                                                          },
                                                      },
                                                  },
                                              }
                                            : {},
                                },
                            }}
                        />
                    )}
                </div>
                <div className="chart-graph-statistics">
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Total</div>
                        <div className="chart-graph-statistics-block-number">
                            {" "}
                            {(statistics.total || 0).toLocaleString()}
                        </div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Average</div>
                        <div className="chart-graph-statistics-block-number">
                            {" "}
                            {(statistics.average || 0).toLocaleString()}
                        </div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Max</div>
                        <div className="chart-graph-statistics-block-number">
                            {(statistics.max || 0).toLocaleString()}
                        </div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Min</div>
                        <div className="chart-graph-statistics-block-number">
                            {(statistics.min || 0).toLocaleString()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FootfallBarChart;
